import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ReactComponent as VerticalDotsIcon } from 'assets/vertical-dots.svg';
import ClientAvatar from 'components/shared/ClientAvatar';
import SectionLoader from 'components/shared/SectionLoader';
import { SortPopover } from 'components/shared/SortPopOver';
import { useAlertStatus } from 'customHook/useAlertStatus';
import { formatDealAmount } from 'helper/short-currency';
import { DealsTableColumns, DealType } from 'model/deals.model';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import RoutePaths from 'routes/RoutePaths';
import { DealsService } from 'service/deals.service';
import zeplinColor, { dealStageColors } from 'theme/Color';
import { Utilities } from 'utilities/utilities';

import { FilterState } from '../../types';
import { filtersToQueryParams } from '../../utils';

import { CustomPagination } from './Pagination';
import { DealsSettings } from '../DealsSettings/DealsSettings';
import { usePermissions } from 'customHook/usePermissions';

export interface Deal {
  title: string;
  stage: string;
  client: {
    clientName: string;
    avatar: string | null;
    accountId: string;
  };
  value: number;
  pipelineType: DealType | string;
  currentStep: string;
  pipelines: string[];
  owner: string;
  closeDate: Date;
  profilePicture: string;
  id?: string;
  dealId?: string;
  needsAttention?: 'warning' | 'onTrack' | 'overdue';
  dealHealth?: string;
  velocityTargetDate?: string | Date;
  [key: string]: any;
}

const SORTABLE_COLUMNS = [
  'name',
  'stageName',
  'client',
  'amount',
  'business',
  'daysInStage',
  'owner',
];

const SORT_LABELS: Record<string, { asc: string; desc: string }> = {
  stageName: { asc: 'Start to End', desc: 'End to Start' },
  amount: { asc: 'Lowest to Highest', desc: 'Highest to Lowest' },
  daysInStage: { asc: 'Lowest to Highest', desc: 'Highest to Lowest' },
  closeDate: { asc: 'Earliest to Latest', desc: 'Latest to Earliest' },
};

interface DealsTableProps {
  filterState: FilterState;
  tabValue: number;
  searchTerm?: string;
  needsAttention?: boolean;
  currentPage: number;
  handleSetCurrentPage: (number: number) => void;
  mineOverride?: boolean;
  clientNameOverride?: string;
}

export const DealsTable = ({
  filterState,
  tabValue,
  searchTerm = '',
  needsAttention = false,
  currentPage,
  handleSetCurrentPage,
  mineOverride,
  clientNameOverride,
}: DealsTableProps) => {
  const { hasAction, hasFullAccess } = usePermissions();
  const canViewDeal = hasAction('deals', 'dealdetails', 'view') || hasFullAccess('deals');
  const isClientScoped = Boolean(clientNameOverride);
  const [sortAnchor, setSortAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);
  const [sortConfig, setSortConfig] = useState<{ field: string; order: 'asc' | 'desc' }>({
    field: '',
    order: 'asc',
  });

  const { startOperation, completeOperation } = useAlertStatus();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const getDeals = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = filtersToQueryParams(
        filterState,
        tabValue,
        searchTerm,
        needsAttention,
        undefined,
        mineOverride,
        clientNameOverride,
      );
      queryParams.page = currentPage + 1;
      queryParams.limit = rowsPerPage;

      if (sortConfig.field) {
        queryParams.sort = `${sortConfig.field}:${sortConfig.order}`;
      }

      const response = await DealsService.getDealsListView(queryParams);
      const mappedDeals = response.data.map((deal: any) => {
        return {
          ...deal,
          owner: deal.owner?.fullName || '',
          dealType: deal.dealType?.dealTypeName || '',
          business: deal.business?.name || '',
        };
      });
      setDeals(mappedDeals);
      setTotalCount(response.total);
    } catch (error) {
      console.error('Failed to fetch deals', error);
      setDeals([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    clientNameOverride,
    currentPage,
    filterState,
    mineOverride,
    needsAttention,
    rowsPerPage,
    searchTerm,
    sortConfig,
    tabValue,
  ]);

  useEffect(() => {
    getDeals();
  }, [getDeals]);

  const [dealCardElement, setDealCardElement] = useState<SVGSVGElement | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<any>(null);

  const handleCloseSettings = useCallback(() => {
    setDealCardElement(null);
    setSelectedDeal(null);
  }, []);

  const handleStageChange = useCallback(() => {
    getDeals();
  }, [getDeals]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      handleSetCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleRowsChange = (newRows: number) => {
    setRowsPerPage(newRows);
    handleSetCurrentPage(0);
  };

  const handleOpenSettings = (event: MouseEvent<SVGSVGElement>, deal: any) => {
    event.stopPropagation();
    setDealCardElement(event.currentTarget);
    setSelectedDeal(deal);
  };

  if (loading) return <SectionLoader />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none', marginTop: '24px' }}>
        <TableContainer
          sx={{
            overflow: 'auto',
            height: isClientScoped
              ? { xs: 'calc(100vh - 560px)', xl: 'calc(100vh - 620px)' }
              : { xs: 'calc(100vh - 450px)', xl: 'calc(100vh - 450px)' },
            border: `1px solid ${zeplinColor.Neutral5}`,
            borderRadius: '14px',
          }}
        >
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow sx={headerRowStyles}>
                {DealsTableColumns.map((column) => {
                  const isSortable = SORTABLE_COLUMNS.includes(column.id);

                  return (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      onClick={(e) =>
                        isSortable && setSortAnchor({ el: e.currentTarget, id: column.id })
                      }
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant='responsiveBody3'>{column.label}</Typography>
                        {isSortable && (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                            aria-label='button'
                          >
                            <UnfoldMoreIcon
                              sx={{
                                fontSize: 16,
                                color: zeplinColor.BasicBlack,
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={DealsTableColumns.length} align='center'>
                    <Typography variant='body2'>Loading deals...</Typography>
                  </TableCell>
                </TableRow>
              ) : deals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={DealsTableColumns.length} align='center'>
                    <Typography variant='body2'>No deals found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                deals.map((row, ind) => {
                  return (
                    <TableRow
                      hover
                      key={ind}
                      component={canViewDeal ? Link : 'div'}
                      to={canViewDeal ? `${RoutePaths.Deals}/${row.dealId}` : undefined}
                      sx={{
                        '&:last-child td, &:last-child th': {
                          borderBottom: `2px solid ${zeplinColor.Neutral5}`,
                        },
                        textDecoration: 'none',
                        color: 'inherit',
                        bgcolor: 'transparent',
                        borderLeft: `2px solid ${zeplinColor.Neutral5}`,
                        cursor: 'pointer',
                      }}
                    >
                      {DealsTableColumns.map((column) => {
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            onClick={(e) => {
                              if (column.id === 'actions') {
                                e.stopPropagation();
                              }
                            }}
                            sx={{
                              padding: '22px 12px',
                              borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                              borderTop: `1px solid ${zeplinColor.Neutral5}`,
                            }}
                          >
                            {renderCell(column.id, row, handleOpenSettings)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <SortPopover
        anchorEl={sortAnchor?.el || null}
        setAnchorEl={() => setSortAnchor(null)}
        sortOption={sortConfig.field === sortAnchor?.id ? sortConfig.order : 'asc'}
        onSortChange={(order) => {
          if (sortAnchor) {
            setSortConfig({ field: sortAnchor.id, order });
            handleSetCurrentPage(0);
          }
          setSortAnchor(null);
        }}
        label={DealsTableColumns.find((c) => c.id === sortAnchor?.id)?.label || ''}
        ascLabel={sortAnchor ? SORT_LABELS[sortAnchor.id]?.asc : undefined}
        descLabel={sortAnchor ? SORT_LABELS[sortAnchor.id]?.desc : undefined}
      />

      <DealsSettings
        anchorElement={dealCardElement}
        onClose={handleCloseSettings}
        startOperation={startOperation}
        completeOperation={completeOperation}
        deal={selectedDeal}
        onDealDeleted={handleStageChange}
        onStageChange={handleStageChange}
      />

      <CustomPagination
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsChange}
      />
    </Box>
  );
};

const headerRowStyles = {
  '& th': { backgroundColor: '#f5f5f5', borderBottom: 'none' },
  '& th:first-of-type': { borderRadius: '14px 0 14px 0px' },
};

const renderCell = (
  columnId: string,
  row: Deal,
  handleOpenSettings: (event: MouseEvent<SVGSVGElement>, deal: any) => void,
) => {
  const attentionStatus = row.needsAttention ? String(row.needsAttention).toLowerCase() : null;
  const health = row.dealHealth || row.dealHealthName;
  const healthLower = health ? String(health).toLowerCase() : '';
  const isOverdue = attentionStatus === 'overdue' || healthLower === 'overdue';
  const isWarning = attentionStatus === 'warning' || healthLower === 'warning';
  const { client } = row || {};

  const renders: Record<string, () => JSX.Element | string> = {
    name: () => (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          maxWidth: '240px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: '8px',
            height: '8px',
            borderRadius: '100%',
            flexShrink: 0,
            backgroundColor: isOverdue
              ? zeplinColor.Error
              : isWarning
                ? zeplinColor.AlertInfo
                : zeplinColor.Success,
          }}
        />
        <Typography noWrap sx={{ textOverflow: 'ellipsis' }} variant='responsiveHeading4'>
          {row.name}
        </Typography>
      </Box>
    ),
    dealType: () => (
      <Chip
        label={
          <Typography variant='responsiveLabel1'>
            {row.dealType ? Utilities.toCamelCase(row.dealType) : 'N/A'}
          </Typography>
        }
        sx={{
          bgcolor: zeplinColor.Neutral4,
          height: '22px',
        }}
      />
    ),
    stageName: () => {
      const stage = row.stageName.toLowerCase();
      const stageColor =
        dealStageColors[stage as keyof typeof dealStageColors] || dealStageColors.null;
      return (
        <Chip
          label={
            <Typography variant='responsiveLabel1' sx={{ color: zeplinColor.Neutral1 }}>
              {stage ? Utilities.toCamelCase(stage) : 'N/A'}
            </Typography>
          }
          sx={{
            bgcolor: stageColor,
            height: '22px',
          }}
        />
      );
    },
    client: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ClientAvatar
          profilePicture={client.avatar ?? ''}
          clientName={client.clientName}
          sx={{ width: '24px', height: '24px' }}
          typographyVariant='responsiveBody3'
        />
        <Typography variant='responsiveBody3'>{client.clientName}</Typography>
      </Box>
    ),
    business: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='responsiveBody3'>{row.business}</Typography>
      </Box>
    ),
    owner: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='responsiveBody3'>{row.owner}</Typography>
      </Box>
    ),
    daysInStage: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='responsiveBody3'>{row.daysInStage}d</Typography>
      </Box>
    ),
    amount: () => {
      return <Typography variant='responsiveHeading5'>{formatDealAmount(row.amount)}</Typography>;
    },
    actions: () => (
      <Box
        sx={{
          padding: '3px',
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          ':hover': {
            backgroundColor: zeplinColor.Neutral5,
            cursor: 'pointer',
          },
          gap: '4px',
        }}
      >
        <VerticalDotsIcon
          style={{
            width: '18px',
            height: '18px',
          }}
          onClick={(e: MouseEvent<SVGSVGElement>) => {
            e.preventDefault();
            handleOpenSettings(e, row);
          }}
        />
      </Box>
    ),
  };

  return renders[columnId] ? renders[columnId]() : (row as any)[columnId];
};
